import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Filter, Loader2, User, UserMinus } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import type { UserSummary } from "../types/tickets";

const UNASSIGNED_VALUE = "unassigned";

interface AssigneeMultiSelectFilterProps {
  users: UserSummary[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  loaded: boolean;
  disabled?: boolean;
}

export function AssigneeMultiSelectFilter({
  users,
  selectedIds,
  onChange,
  loaded,
  disabled = false,
}: AssigneeMultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Ref-backed selection so cmdk's cached onSelect handlers always see fresh state.
  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const toggle = useCallback(
    (id: string) => {
      const current = selectedIdsRef.current;
      const next = current.includes(id)
        ? current.filter((v) => v !== id)
        : [...current, id];
      onChange(next);
    },
    [onChange]
  );

  const clearAll = useCallback(() => onChange([]), [onChange]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const hasSelection = selectedIds.length > 0;

  // Alphabetical sort; client-side substring filter on name + email.
  // Sorting only by name (not selected-first) prevents rows from jumping on toggle.
  const filteredUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => a.fullName.localeCompare(b.fullName));
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (u) =>
        u.fullName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [users, debouncedSearch]);

  // "Unassigned" stays visible regardless of the search query unless the search
  // explicitly excludes it (e.g., user types something that doesn't match "unassigned").
  // For consistency with how customers expect the row to behave, we always show it
  // unless the typed query is non-empty AND doesn't appear in "unassigned"/"no agent".
  const unassignedMatchesSearch = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return true;
    return "unassigned".includes(q) || "no agent assigned".includes(q);
  }, [debouncedSearch]);

  const unassignedChecked = selectedIdSet.has(UNASSIGNED_VALUE);

  const showLoadingState = !loaded && users.length === 0;
  const showEmptyOrgsState = loaded && users.length === 0;
  const showNoMatchesState =
    loaded &&
    users.length > 0 &&
    filteredUsers.length === 0 &&
    !unassignedMatchesSearch;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between transition-all duration-200",
            hasSelection && "border-primary border-2 bg-primary/5"
          )}
        >
          <div className="flex items-center min-w-0">
            <Filter
              className={cn(
                "mr-2 h-4 w-4 flex-shrink-0",
                hasSelection ? "text-primary" : "text-muted-foreground"
              )}
            />
            <span className="truncate">
              Assignee
              {hasSelection && ` (${selectedIds.length})`}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search assignees..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          {hasSelection && (
            <div className="flex items-center justify-between px-2 py-1.5 border-b">
              <span className="text-xs text-muted-foreground">
                {selectedIds.length} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  clearAll();
                }}
              >
                Clear
              </Button>
            </div>
          )}
          <CommandList>
            {showLoadingState ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="mx-auto h-4 w-4 animate-spin mb-2" />
                Loading agents...
              </div>
            ) : showEmptyOrgsState ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No agents in the selected organizations
              </div>
            ) : (
              <CommandGroup>
                {unassignedMatchesSearch && (
                  <CommandItem
                    key={UNASSIGNED_VALUE}
                    value={UNASSIGNED_VALUE}
                    onSelect={() => toggle(UNASSIGNED_VALUE)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted flex-shrink-0">
                        <UserMinus className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">Unassigned</p>
                        <p className="text-xs text-muted-foreground truncate">
                          No agent assigned
                        </p>
                      </div>
                      {unassignedChecked && (
                        <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                      )}
                    </div>
                  </CommandItem>
                )}
                {filteredUsers.map((user) => {
                  const checked = selectedIdSet.has(user.id);
                  return (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={() => toggle(user.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted flex-shrink-0">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{user.fullName}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                        </div>
                        {checked && (
                          <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
                {showNoMatchesState && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No agents found for "{debouncedSearch}"
                  </div>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
